import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/AppLayout";
import { api } from "@/lib/api";
import DateNav, { todayISO } from "@/components/DateNav";

const BELT_CLASS = {
  white: "belt-white", blue: "belt-blue", purple: "belt-purple",
  brown: "belt-brown", black: "belt-black",
};

function weekdayFromISO(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  const js = new Date(y, m - 1, d).getDay(); // 0=Sun ... 6=Sat
  return js === 0 ? 6 : js - 1;              // 0=Mon ... 6=Sun
}

function StatusButtons({ status, onChange, sid }) {
  return (
    <div className="flex gap-1.5">
      {["in", "late", "out"].map(s => (
        <button key={s} onClick={() => onChange(s)} data-testid={`status-${sid}-${s}`}
          className="pill"
          style={status === s
            ? (s === "in"
              ? { background: "var(--green-bg)", color: "var(--green)", border: "1px solid var(--green-border)" }
              : s === "late"
                ? { background: "var(--warn-bg)", color: "var(--warn)", border: "1px solid rgba(244,185,66,0.3)" }
                : { background: "rgba(255,91,91,0.12)", color: "var(--danger)", border: "1px solid rgba(255,91,91,0.3)" })
            : { background: "transparent", color: "var(--text-muted)", border: "1px solid var(--border)" }}>
          {s}
        </button>
      ))}
    </div>
  );
}

/** Data + effects for the picked date/class: schedule for the weekday, roster, and the rolling 30d trend. */
function useAttendanceData(date, urlClassId) {
  const [classes, setClasses] = useState([]);
  const [selectedId, setSelectedId] = useState(urlClassId || null);
  const [rosterData, setRosterData] = useState(null);
  const [trend, setTrend] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/classes", { params: { date } }).then(r => {
      if (cancelled) return;
      const dow = weekdayFromISO(date);
      const list = (r.data || [])
        .filter(c => c.day_of_week === dow)
        .sort((a, b) => a.start_time.localeCompare(b.start_time));
      setClasses(list);
      setSelectedId(prev => {
        if (prev && list.some(c => c.class_id === prev)) return prev;
        return list[0]?.class_id || null;
      });
    });
    return () => { cancelled = true; };
  }, [date]);

  useEffect(() => {
    if (!selectedId) { setRosterData(null); return; }
    let cancelled = false;
    api.get(`/attendance/roster?class_id=${selectedId}&date=${date}`).then(r => {
      if (!cancelled) setRosterData(r.data);
    });
    return () => { cancelled = true; };
  }, [selectedId, date]);

  useEffect(() => {
    api.get("/attendance/trend").then(r => setTrend(r.data));
  }, []);

  const mark = async (student_id, status) => {
    if (!selectedId) return;
    await api.post("/attendance/mark", { student_id, class_id: selectedId, status, date });
    const { data } = await api.get(`/attendance/roster?class_id=${selectedId}&date=${date}`);
    setRosterData(data);
  };

  return { classes, selectedId, setSelectedId, rosterData, trend, mark };
}

function ClassTabs({ classes, selectedId, onSelect }) {
  if (classes.length === 0) {
    return (
      <div className="label-tiny" style={{ color: "var(--text-dim)" }} data-testid="attendance-no-classes">
        No classes scheduled for this day.
      </div>
    );
  }
  return classes.map(c => {
    const active = c.class_id === selectedId;
    return (
      <button key={c.class_id}
        onClick={() => onSelect(c.class_id)}
        data-testid={`attendance-tab-${c.class_id}`}
        style={{
          whiteSpace: "nowrap", padding: "10px 14px", borderRadius: 10,
          border: `1px solid ${active ? "var(--green)" : "var(--border)"}`,
          background: active ? "rgba(61,220,126,0.1)" : "var(--bg-card)",
          color: active ? "var(--green)" : "var(--text)",
          fontSize: 13, fontWeight: 600, flexShrink: 0,
        }}>
        <span className="mono" style={{ marginRight: 6, fontSize: 12 }}>{c.start_time}</span>
        {c.name}
      </button>
    );
  });
}

function RosterRow({ r, onMark }) {
  return (
    <div key={r.student_id} className="flex items-center gap-4 py-2" data-testid={`roster-${r.student_id}`}>
      <div className="w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-semibold"
        style={{ background: "var(--bg-elev)", border: "1px solid var(--border)" }}>
        {r.initials}
      </div>
      <div className="flex-1">
        <div className="font-semibold text-[14px] uppercase tracking-tight">{r.name}</div>
        <div className="flex items-center gap-2 mt-1">
          <span className={`belt-bar ${BELT_CLASS[r.belt] || "belt-white"}`} style={{ width: 24, height: 6 }} />
          <span className="label-tiny capitalize">{r.belt} · {r.stripes}</span>
        </div>
      </div>
      <StatusButtons status={r.status} sid={r.student_id} onChange={(s) => onMark(r.student_id, s)} />
    </div>
  );
}

function ClassRosterPanel({ date, klass, rosterData, checkedIn, onMark }) {
  if (!klass) {
    return (
      <div className="label" style={{ color: "var(--text-dim)" }} data-testid="attendance-empty">
        Pick a class above to view attendance.
      </div>
    );
  }
  if (!rosterData) return null;
  return (
    <>
      <div className="flex items-start justify-between">
        <div>
          <div className="label" style={{ color: "var(--green)" }}>Attendance · {date}</div>
          <div className="text-[22px] font-bold uppercase mt-1 tracking-tight">{klass.name} · {klass.start_time}</div>
          <div className="label-tiny mt-1">{klass.instructor} · {klass.mat}</div>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-4 justify-end">
            <div>
              <div className="label-tiny" style={{ fontSize: 10 }}>Reserved</div>
              <div className="text-[20px] font-bold" style={{ color: "var(--text-muted)" }} data-testid="attendance-reserved">
                {rosterData.counts.reserved ?? 0}
              </div>
            </div>
            <div style={{ width: 1, height: 34, background: "var(--border)" }} />
            <div>
              <div className="label-tiny" style={{ fontSize: 10 }}>Checked in</div>
              <div className="text-[28px] font-bold">
                <span style={{ color: "var(--green)" }} data-testid="attendance-checkedin">{checkedIn}</span>
                <span style={{ color: "var(--text-dim)" }}>/{klass.capacity}</span>
              </div>
            </div>
          </div>
          <div className="label-tiny mt-1" style={{ color: "var(--text-dim)", fontSize: 10 }}>
            Reservations are pre-bookings · tap a row to mark arrival
          </div>
        </div>
      </div>
      <div className="divider my-5" />
      {rosterData.roster.length === 0 && (
        <div className="label" style={{ color: "var(--text-dim)" }}>No students in the academy roster yet.</div>
      )}
      <div className="space-y-2">
        {rosterData.roster.map(r => <RosterRow key={r.student_id} r={r} onMark={onMark} />)}
      </div>
    </>
  );
}

function ClassStatsCard({ trend }) {
  return (
    <div className="card p-6">
      <div className="label mb-4">This class · stats</div>
      {trend ? (
        <>
          <div className="flex justify-between py-1.5"><span className="label-tiny">Average (30d)</span><span className="font-semibold">{trend.average}</span></div>
          <div className="flex justify-between py-1.5"><span className="label-tiny">Last 4 days</span><span className="mono">{trend.last4_actual}{trend.last4_target != null ? ` / ${trend.last4_target}` : ""}</span></div>
          <div className="flex justify-between py-1.5"><span className="label-tiny">No-shows total</span><span className="font-semibold">{trend.no_shows}</span></div>
        </>
      ) : <div className="label-tiny">Loading…</div>}
    </div>
  );
}

function AttendanceTrendChart({ trend }) {
  const max = Math.max(...trend.days_30, 1);
  return (
    <div className="card p-6">
      <div className="label mb-5">Academy attendance · 30 days</div>
      <div className="flex items-end gap-1.5 h-36">
        {trend.days_30.map((v, i) => {
          const daysAgo = trend.days_30.length - 1 - i;
          const barDate = new Date(Date.now() - daysAgo * 86400000).toISOString().slice(0,10);
          return (
            <div key={barDate} className="bar flex-1 rounded-t-sm"
              style={{
                height: `${(v / max) * 100}%`,
                background: i === trend.today_index ? "var(--green)" : "var(--border-strong)",
              }} />
          );
        })}
      </div>
    </div>
  );
}

/**
 * Owner attendance view — mirrors CoachCheckin's date+class navigation but keeps
 * the desktop 3-column layout (roster + QR + academy stats).
 */
export default function Attendance() {
  const [params, setParams] = useSearchParams();
  const urlClassId = params.get("class_id");
  const urlDate = params.get("date");
  const [date, setDate] = useState(urlDate || todayISO());
  const { classes, selectedId, setSelectedId, rosterData, trend, mark } = useAttendanceData(date, urlClassId);

  // If DateNav changes, drop the URL params so we don't keep re-forcing a class
  // that isn't on the newly-picked weekday.
  const onDateChange = (d) => {
    setDate(d);
    if (params.get("class_id") || params.get("date")) setParams({});
  };

  const checkedIn = useMemo(() => {
    if (!rosterData) return 0;
    return rosterData.roster.filter(r => r.status === "in" || r.status === "late").length;
  }, [rosterData]);

  const klass = rosterData?.klass;

  return (
    <div data-testid="page-attendance">
      <PageHeader section="Operate" title="Attendance"
        right={<DateNav date={date} onChange={onDateChange} testid="attendance-datenav" />}
      />
      <div className="p-4 md:p-8 space-y-6">
        <div className="flex gap-2 overflow-x-auto pb-2" data-testid="attendance-class-tabs">
          <ClassTabs classes={classes} selectedId={selectedId} onSelect={setSelectedId} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="card p-6 lg:col-span-2">
            <ClassRosterPanel date={date} klass={klass} rosterData={rosterData} checkedIn={checkedIn} onMark={mark} />
          </div>
          <div className="space-y-4">
            <ClassStatsCard trend={trend} />
          </div>
        </div>

        {trend && <AttendanceTrendChart trend={trend} />}
      </div>
    </div>
  );
}
